import {
  ChangeDetectionStrategy,
  Component,
  type ComponentRef,
  ElementRef,
  EventEmitter,
  Input,
  type OnChanges,
  type OnDestroy,
  type OnInit,
  Output,
  viewChild,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { RcpPopoverDirective } from '@reconcept-platform/shared/ui/standalone/popover';
import { Observable, type Observer, type Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SearchResultsComponent, type SelectableSearchResult } from './search-results/search-results.component';

@Component({
  selector: 'rcp-search-bar',
  template: `
    @if (showSearchBar) {
      <div class="search-bar">
        @if (showSearchIcon) {
          <fa-icon [icon]="faSearch"></fa-icon>
        }
        <input
          [class.no-icon]="!showSearchIcon"
          type="text"
          [maxlength]="maxLength"
          #searchStringInput
          [placeholder]="placeholder ?? ('qz-type-here-to-search-zm' | translate)"
          [(ngModel)]="searchString"
          (ngModelChange)="searchStringChange()"
          (focus)="focusInput()"
          [rcpPopover]="searchResults"
          [rcpPopoverIsEnabled]="useResultsDropdown"
          rcpPopoverVariation="menuOverModal"
          rcpPopoverTrigger="manual"
          [rcpPopoverUseHostWidth]="true"
        />

        <ng-template #searchResults>
          <rcp-search-results [results]="results" (clickResult)="clickResult.emit($event)" (hide)="hide()" />
        </ng-template>
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }

    .search-bar {
      position: relative;

      fa-icon {
        position: absolute;
        left: 17px;
        top: 14px;
        color: var(--col-primary);
      }

      input {
        padding-left: 40px;

        &.no-icon {
          padding-left: var(--legacy-default-element-spacing);
        }
      }
    }
  `,
  imports: [FaIconComponent, FormsModule, TranslatePipe, SearchResultsComponent, RcpPopoverDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SearchBarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() results: SelectableSearchResult[];
  @Input() debounceTime = 300;
  @Input() placeholder: string;
  @Input() searchString: string;
  @Input() showSearchBar = true;
  @Input() showSearchIcon = true;
  @Input() useResultsDropdown = true;
  @Input() maxLength: number;

  @Output() clickResult = new EventEmitter<SelectableSearchResult>();
  @Output() changeSearchString = new EventEmitter<string>();

  @ViewChild('searchStringInput', { static: true, read: ElementRef })
  searchStringInput: ElementRef;

  popover = viewChild(RcpPopoverDirective);

  private searchResultsComponent: ComponentRef<SearchResultsComponent>;

  private debounceObserver: Observer<string>;
  private debounceSubscription: Subscription;

  ngOnInit() {
    this.init();
  }

  ngOnChanges(changes: any) {
    if (changes.searchString) {
      this.searchStringChange();
    }

    //searchString may be undefined, in which case we do want to show results
    if (changes.results && this.results && this.results.length && this.searchString !== '') {
      this.showSearchResults();
    }
  }

  ngOnDestroy() {
    if (this.debounceSubscription) {
      this.debounceSubscription.unsubscribe();
    }
  }

  searchStringChange() {
    if (this.debounceObserver) {
      this.debounceObserver.next(this.searchString);
    }
  }

  onClickResult(result: SelectableSearchResult) {
    this.clickResult.emit(result);
  }

  focusInput() {
    this.showSearchResults();
  }

  showSearchResults() {
    // create results view
    if (this.useResultsDropdown) {
      // @ts-expect-error TS2532 object possibly undefined
      this.popover().show();
    }
  }

  hide() {
    this.popover()?.hide();
  }

  private init() {
    // create a debouncing observable that eventually emits the change event
    this.debounceSubscription = Observable.create((observer: any) => {
      this.debounceObserver = observer;
    })
      .pipe(debounceTime(this.debounceTime))
      .subscribe((searchString: string) => {
        this.changeSearchString.emit(searchString);
      });

    // if searchstring is initially set, use it immediately
    if (this.searchString) {
      this.searchStringChange();
    }
  }

  protected readonly faSearch = faSearch;
}
